import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, FileText, X } from 'lucide-react';
import { formatDisplayDate } from '../../../utils/dateInput';

const isImage = (doc) => (doc?.mimeType || '').startsWith('image/');

/**
 * Uploaded application documents as a thumbnail grid; images open in a
 * lightbox (arrow keys to page), PDFs open in a new tab. Preview URLs are
 * short-lived signed URLs from the server, so the page reloads them on open.
 */
const DocumentGallery = ({ documents = [] }) => {
  const [index, setIndex] = useState(null);
  const images = documents.filter(isImage);
  const current = index !== null ? images[index] : null;

  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setIndex(null);
      if (event.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length);
      if (event.key === 'ArrowLeft') setIndex((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, images.length]);

  if (!documents.length) return <p className="text-sm text-gray-500">No documents uploaded.</p>;

  return (
    <>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {documents.map((doc) => (
          <li key={doc.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            {isImage(doc) ? (
              <button type="button" onClick={() => setIndex(images.indexOf(doc))} className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={`View ${doc.label}`}>
                <div className="flex h-28 items-center justify-center bg-gray-100">
                  {doc.previewUrl ? <img src={doc.previewUrl} alt="" className="h-full w-full object-cover" /> : <FileText className="h-8 w-8 text-gray-400" aria-hidden="true" />}
                </div>
                <DocCaption doc={doc} />
              </button>
            ) : (
              <a href={doc.previewUrl || undefined} target="_blank" rel="noreferrer" className="block focus:outline-none focus:ring-2 focus:ring-blue-500">
                <div className="flex h-28 flex-col items-center justify-center gap-1 bg-gray-100 text-gray-500">
                  <FileText className="h-8 w-8" aria-hidden="true" />
                  <span className="inline-flex items-center gap-1 text-xs"><ExternalLink className="h-3 w-3" aria-hidden="true" /> Open PDF</span>
                </div>
                <DocCaption doc={doc} />
              </a>
            )}
          </li>
        ))}
      </ul>

      {current && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-950/90" role="dialog" aria-modal="true" aria-label={current.label}>
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-white">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{current.label}</p>
              <p className="truncate text-xs text-gray-300">{current.originalFileName} · {index + 1} of {images.length}</p>
            </div>
            <div className="flex items-center gap-2">
              <a href={current.previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-white/30 px-3 py-1.5 text-xs hover:bg-white/10">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Full size
              </a>
              <button type="button" onClick={() => setIndex(null)} className="rounded-md p-1.5 hover:bg-white/10" aria-label="Close">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6" onClick={() => setIndex(null)}>
            <img src={current.previewUrl} alt={current.label} className="max-h-full max-w-full rounded object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
            {images.length > 1 && (
              <>
                <button type="button" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + images.length) % images.length); }} className="absolute left-3 rounded-full bg-black/50 p-2 text-white hover:bg-black/70" aria-label="Previous document">
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % images.length); }} className="absolute right-3 rounded-full bg-black/50 p-2 text-white hover:bg-black/70" aria-label="Next document">
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const DocCaption = ({ doc }) => (
  <div className="px-2 py-1.5">
    <p className="truncate text-xs font-medium text-gray-800">{doc.label}</p>
    <p className="truncate text-[11px] text-gray-500">{doc.originalFileName}{doc.uploadedAt ? ` · ${formatDisplayDate(new Date(doc.uploadedAt))}` : ''}</p>
  </div>
);

export default DocumentGallery;
